import http from '../lib/http'

export interface PasskeyMetadata {
    id: string
    credential_id: string
    user_id: string
    device_name: string
    device_type?: string
    browser?: string
    operating_system?: string
    is_synced?: boolean
    last_used?: string
    created_at: string
    updated_at: string
}

interface CreateMetadataParams {
    credentialId: string
    deviceName: string
    browser?: string
    operating_system?: string
    device_type?: string
    is_synced?: boolean
    sync_provider?: string
    authenticator_attachment?: string
    platform?: string
    screen_resolution?: string
    timezone?: string
    language?: string
}

class PasskeyMetadataService {
    /**
     * Create or update passkey metadata with enhanced device info
     */
    static async upsert(credentialId: string, deviceName: string): Promise<PasskeyMetadata>
    static async upsert(params: CreateMetadataParams): Promise<PasskeyMetadata>
    static async upsert(
        credentialIdOrParams: string | CreateMetadataParams,
        deviceName?: string
    ): Promise<PasskeyMetadata> {
        let payload: CreateMetadataParams

        if (typeof credentialIdOrParams === 'string') {
            // Legacy call with just credentialId and deviceName
            payload = {
                credentialId: credentialIdOrParams,
                deviceName: deviceName!
            }
        } else {
            // New call with full params
            payload = credentialIdOrParams
        }

        const data = await http.postRequest<{ metadata: PasskeyMetadata }>('/passkeys/metadata', payload)
        return data.metadata
    }

    /**
     * Get all passkey metadata for current user
     */
    static async getAll(): Promise<PasskeyMetadata[]> {
        const data = await http.getRequest<{ metadata: PasskeyMetadata[] }>('/passkeys/metadata')
        return data.metadata
    }

    /**
     * Update device name for a credential
     */
    static async updateDeviceName(credentialId: string, deviceName: string): Promise<PasskeyMetadata> {
        const data = await http.putRequest<{ metadata: PasskeyMetadata }>(`/passkeys/metadata/${encodeURIComponent(credentialId)}`, {
            deviceName
        })
        return data.metadata
    }

    /**
     * Delete metadata for a credential
     */
    static async delete(credentialId: string): Promise<boolean> {
        const data = await http.deleteRequest<{ success: boolean }>(`/passkeys/metadata/${encodeURIComponent(credentialId)}`)
        return data.success
    }

    /**
     * Update last used timestamp
     */
    static async updateLastUsed(credentialId: string): Promise<PasskeyMetadata> {
        const data = await http.postRequest<{ metadata: PasskeyMetadata }>(`/passkeys/metadata/${encodeURIComponent(credentialId)}/used`)
        return data.metadata
    }

    /**
     * Clean up orphaned metadata
     */
    static async cleanupOrphaned(activeCredentialIds: string[]): Promise<number> {
        const data = await http.postRequest<{ deleted: number }>('/passkeys/metadata/cleanup', {
            activeCredentialIds
        })
        return data.deleted
    }
}

export default PasskeyMetadataService
